import type { AlertSeverity, AlertState } from '../model/alertState.types'
import type { OrderDetails } from '@/entities/order/model'
import type { FeatureToggles, StateDefinition } from '@/entities/state/model'

/**
 * Calculates the elapsed time in seconds since a given start time
 *
 * @param startTime - Primary time to use for calculation
 * @param fallbackTime - Fallback time if startTime is not available
 * @param currentTime - Current time (defaults to now)
 * @returns Elapsed time in seconds
 */
export function calculateElapsedSeconds(
  startTime: string | undefined,
  fallbackTime: string | undefined,
  currentTime: Date = new Date(),
): number {
  const timeToUse = startTime || fallbackTime
  if (!timeToUse)
    return 0

  return (currentTime.getTime() - new Date(timeToUse).getTime()) / 1000
}

/**
 * Determines the alert severity based on elapsed time and thresholds
 *
 * @param elapsedSeconds - Time elapsed in seconds
 * @param warningThreshold - Threshold for warning severity in seconds
 * @param errorThreshold - Threshold for error severity in seconds
 * @returns The appropriate alert severity
 */
export function determineAlertSeverity(
  elapsedSeconds: number,
  warningThreshold: number,
  errorThreshold: number,
): AlertSeverity {
  if (elapsedSeconds >= errorThreshold)
    return 'error'
  if (elapsedSeconds >= warningThreshold)
    return 'warning'

  return 'success'
}

/**
 * Formats elapsed time in a human-readable format
 *
 * @param elapsedSeconds - Time elapsed in seconds
 * @returns Formatted time string (e.g., "5m 30s")
 */
export function formatElapsedTime(elapsedSeconds: number): string {
  if (elapsedSeconds < 60)
    return `${Math.floor(elapsedSeconds)}s`

  const minutes = Math.floor(elapsedSeconds / 60)
  const seconds = Math.floor(elapsedSeconds % 60)

  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`
}

/**
 * Determines if an order is in a wait time start state
 *
 * @param orderStatus - Current order status
 * @param stateFeatures - Feature toggles configuration
 * @returns Whether the order is in a wait time start state
 */
export function isInWaitTimeStartState(
  orderStatus: string,
  stateFeatures?: FeatureToggles,
): boolean {
  return orderStatus === 'Arrived'
    || (Array.isArray(stateFeatures?.waitTime?.startState)
      && stateFeatures?.waitTime?.startState.includes(orderStatus))
}

/**
 * Calculates the alert state for an order based on its current state and elapsed time
 *
 * @param props - Properties for the hook
 * @param props.order - Order details
 * @param props.targetState - Current state definition i.e. the state the order is in
 * @param props.stateFeatures - State defined feature toggles
 * @param props.currentTime - Current time (defaults to now)
 * @returns Alert state object
 */
export function calculateAlertState({
  order,
  targetState,
  stateFeatures,
  currentTime,
}: {
  order: OrderDetails
  targetState: StateDefinition | undefined
  stateFeatures: FeatureToggles | undefined
  currentTime: Date
}): AlertState {
  const defaultAlertState: AlertState = {
    severity: 'none',
    elapsedTime: 0,
    thresholdTime: 0,
    showAnimation: false,
  }

  if (!targetState)
    return defaultAlertState

  // Determine if the order should animate
  const showAnimation = targetState.canAcknowledge && !order.isAcknowledged

  const elapsedTime = calculateElapsedSeconds(
    order.waitTimeStart,
    order.lastTransitionTime,
    currentTime,
  )

  // Handle consolidation alerts
  if (targetState.notificationTypes?.includes('consolidation')
    && order.hasNotified?.includes('consolidation')) {
    return {
      severity: 'consolidation',
      elapsedTime,
      thresholdTime: 0,
      showAnimation,
    }
  }

  // Handle state-defined alert timers
  if (targetState.displayState?.alertTimers?.length) {
    const alertTimers = targetState.displayState.alertTimers

    // Find the appropriate timer based on elapsed time
    const targetAlertTimer = alertTimers.find((_alert, index) =>
      index < alertTimers.length - 1
      && alertTimers[index + 1].time >= elapsedTime,
    ) || alertTimers[alertTimers.length - 1]

    return {
      severity: targetAlertTimer.color.includes('success')
        ? 'success'
        : targetAlertTimer.color.includes('warning')
          ? 'warning'
          : 'error',
      elapsedTime,
      thresholdTime: targetAlertTimer.time,
      showAnimation,
    }
  }

  // Handle special wait time states
  if (isInWaitTimeStartState(order.status, stateFeatures)) {
    const waitTime = stateFeatures?.waitTime

    // Default success state
    if (!waitTime?.alert || elapsedTime < waitTime.alert) {
      return {
        severity: 'success',
        elapsedTime,
        thresholdTime: 0,
        showAnimation,
      }
    }

    // Warning state
    if (waitTime.goal && elapsedTime < waitTime.goal) {
      return {
        severity: 'warning',
        elapsedTime,
        thresholdTime: waitTime.alert,
        showAnimation,
      }
    }

    // Error state
    return {
      severity: 'error',
      elapsedTime,
      thresholdTime: waitTime.goal as number,
      showAnimation,
    }
  }

  return defaultAlertState
}
